<template>
  <VLayout>
    <VFlex xs8>
      <VCard style="height: calc(100vh - 100px)">
        <VLayout justify-space-around row>
          <VFlex xs6>
            <VCardTitle style="font-variant: small-caps">
              <h1>Email naslovi</h1>
              <v-spacer />
              <v-text-field
                v-model="query"
                label="Iskanje po pošiljatelju"
                single-line
                hide-details
              />
              <v-btn color="primary" @click="search"> Išči </v-btn>
            </VCardTitle>
            <VContainer class="pa-0" fill-height fluid>
              <VLayout fill-height>
                <VFlex xs12>
                  <VDataTable
                    :headers="headers"
                    :items="emailMappings"
                    rows-per-page-text="Vrstic na stran"
                  >
                    <template slot="items" slot-scope="props">
                      <td class="text-xs">
                        {{ props.item.email }}
                      </td>
                      <td class="text-xs">
                        {{ props.item.emails_to_bounce.join(', ') }}
                      </td>
                      <td class="text-xs-center">
                        <VTooltip top>
                          <VBtn
                            slot="activator"
                            flat
                            icon
                            color="green"
                            @click.stop="editEmailMapping(props.item)"
                          >
                            <VIcon>edit</VIcon>
                          </VBtn>
                          <span>Uredite pravilo</span>
                        </VTooltip>
                        <VTooltip top>
                          <VBtn
                            slot="activator"
                            flat
                            icon
                            color="red"
                            @click.stop="deleteEmailMapping(props.item)"
                          >
                            <VIcon>delete</VIcon>
                          </VBtn>
                          <span>Izbrišite pravilo</span>
                        </VTooltip>
                      </td>
                    </template>
                    <template slot="no-data"> Sorry, nothing to display here :( </template>
                  </VDataTable>
                </VFlex>
              </VLayout>
            </VContainer>
          </VFlex>
        </VLayout>
      </VCard>
    </VFlex>
    <VFlex xs4>
      <VCard style="height: calc(100vh - 100px); overflow-y: scroll">
        <VLayout justify-space-around row>
          <VFlex xs10>
            <VCardTitle style="padding: 10px 0 0 0; font-variant: small-caps; margin-bottom: 10px">
              <h2>{{ currentActionText }}</h2>
            </VCardTitle>
            <VContainer fluid style="padding: 0 0 0 10px">
              <VLayout fill-height row wrap>
                <VFlex xs12>
                  <VTextField
                    v-model="emailMappingObject.email"
                    label="Email naslov"
                    type="string"
                  />
                </VFlex>
                <VCombobox
                  v-model="emailMappingObject.emails_to_bounce"
                  deletable-chips
                  label="Preusmeritveni email naslovi"
                  multiple
                  chips
                />
                <v-flex xs12 style="display: flex; justify-content: flex-end">
                  <VBtn v-if="editing" @click.stop="stopEditing()"> Prekliči urejanje </VBtn>
                  <VBtn
                    color="primary"
                    :disabled="
                      !emailMappingObject.email || !emailMappingObject.emails_to_bounce.length
                    "
                    @click="createNewEmailMapping()"
                  >
                    Potrdi
                  </VBtn>
                </v-flex>
                <v-alert
                  :value="emailErrors"
                  transition="scroll-x-transition"
                  type="warning"
                  outline
                  style="margin: 0px; width: 98%"
                >
                  {{ emailErrors }}
                </v-alert>
              </VLayout>
            </VContainer>
          </VFlex>
        </VLayout>
      </VCard>
    </VFlex>
  </VLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { emailRegex } from '@/helper';
import { cloneDeep } from 'lodash';

export default {
  name: 'EmailMapping',
  data() {
    return {
      headers: [
        {
          text: 'Email pošiljatelja',
          align: 'left',
          sortable: false,
        },
        {
          text: 'Preusmeritveni email naslovi',
          align: 'left',
          sortable: false,
        },
        {
          text: 'Akcije',
          align: 'middle',
          sortable: false,
        },
      ],
      editing: false,
      editingActionText: 'Uredi vnos',
      createNewActionText: 'Dodaj vnos',
      currentActionText: '',
      emailErrors: null,
      emailMappingObject: {
        email: '',
        emails_to_bounce: [],
      },
      emptyEmailMappingsObject: {
        email: '',
        emails_to_bounce: [],
      },
      query: '',
    };
  },
  computed: {
    ...mapGetters({
      emailMappings: 'emailMappings',
    }),
  },
  watch: {},
  mounted() {
    // Get data into emailMappings[] from vuex store
    this.getEmailMappingsAction({});
    this.currentActionText = this.createNewActionText;
  },
  methods: {
    ...mapActions([
      'setSnackBarStateAction',
      'getEmailMappingsAction',
      'createEmailMappingAction',
      'editEmailMappingAction',
      'removeEmailMappingAction',
    ]),
    showSnackbar(successful, msg) {
      const snackbarColor = successful ? 'green' : 'red';
      this.setSnackBarStateAction({
        snackBar: true,
        color: snackbarColor,
        text: msg,
      });
    },
    verifyEmails() {
      const mail = this.emailMappingObject.email;

      // Check the main email
      const emailValid = emailRegex.test(mail);
      // Check the mappings
      let mappingsValid = false;
      const valueMaps = [
        ...new Set(this.emailMappingObject.emails_to_bounce.map((v) => emailRegex.test(v))),
      ];
      if (valueMaps.length === 1 && valueMaps[0]) {
        mappingsValid = true;
      }

      return emailValid && mappingsValid;
    },
    editEmailMapping(emailMapping) {
      this.emailMappingObject = cloneDeep(emailMapping);
      this.editing = true;
      this.currentActionText = this.editingActionText;
    },
    stopEditing() {
      this.emailMappingObject = cloneDeep(this.emptyEmailMappingsObject);
      this.editing = false;
      this.currentActionText = this.createNewActionText;
    },
    async search() {
      await this.getEmailMappingsAction({ email: this.query });
    },
    // ================================ CRUD operations ================================
    async createNewEmailMapping() {
      // Trim the leading/trailing whitespaces from all emails
      this.emailMappingObject.email = this.emailMappingObject.email.trim();
      for (let i = 0; i < this.emailMappingObject.emails_to_bounce.length; i += 1) {
        // eslint-disable-next-line max-len
        this.emailMappingObject.emails_to_bounce[i] =
          this.emailMappingObject.emails_to_bounce[i].trim();
      }
      // Remove duplicates in emails_to_bounce to avoid combobox duplicate error
      // eslint-disable-next-line max-len
      this.emailMappingObject.emails_to_bounce = [
        ...new Set(this.emailMappingObject.emails_to_bounce),
      ];

      // Validate the emails
      if (!this.verifyEmails()) {
        this.emailErrors = 'Vsi naslovi niso pravilne oblike.';
        // Hide the v-alert after timeout
        setTimeout(() => {
          this.emailErrors = null;
        }, 3000);
        return;
      }
      this.emailErrors = null;

      try {
        if (this.editing) {
          await this.editEmailMappingAction(this.emailMappingObject);
          this.showSnackbar(true, 'Preusmeritev je bila uspešno posodobljena.');
          this.stopEditing();
        } else {
          await this.createEmailMappingAction(this.emailMappingObject);
          this.showSnackbar(true, 'Dodajanje preusmeritve je bilo uspešno.');
          this.emailMappingObject = cloneDeep(this.emptyEmailMappingsObject);
        }
      } catch (err) {
        // Check for duplicate email error
        let message;
        if (
          err.response.data.email &&
          err.response.data.email[0] === 'customer email mapping s tem email že obstaja.'
        ) {
          message = 'Preusmeritev za ta izvorni email že obstaja.';
        } else {
          const typeMessage = this.editing ? 'urejanju' : 'dodajanju';
          message = `Napaka pri ${typeMessage} preusmeritev\n. Poskusite znova ali pokličite administratorja.`;
        }
        this.showSnackbar(false, message);
      }
    },
    async deleteEmailMapping(emailMapping) {
      try {
        await this.removeEmailMappingAction(emailMapping);
        this.showSnackbar(
          true,
          `Uspešno ste izbrisali preusmeritve za email: ${emailMapping.email}`
        );
      } catch (err) {
        this.showSnackbar(
          false,
          'Napaka pri brisanju preusmeritev. Poskusite znova ali pokličite administratorja.'
        );
      }
    },
    // ================================/ CRUD operations ================================
  },
};
</script>

<style scoped>
.v-text-field {
  padding: 0;
}

::v-deep .v-table__overflow {
  overflow: auto;
  max-height: 70vh;
}
</style>
